<script setup lang="ts">
import { formatDateTime, formatTime } from '@/composables/useFormatDate';
import { JobStatuses } from '@/infrastructure/enums';
import { toTitleCase } from '@/infrastructure/helpers';
import type { ImporterDetail, Job } from '@/infrastructure/types';
import { checkHealth, useCreate, useFetch } from '@/plugins/api';
import { useDefaultStore } from '@/stores/defaultStore';
import { ArrowRouting20Regular, Code20Regular } from '@vicons/fluent';
import { NButton, NCode, NFlex, NIcon, NPopover, NSpin, NTag, NTooltip, type DataTableColumn } from 'naive-ui';
import { formatDistanceStrict } from 'date-fns'
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/userStore';

let interval: NodeJS.Timeout
const refreshIntervalInSeconds = 30
const defaultStore = useDefaultStore()
const { isSuperAdmin } = storeToRefs(useUserStore())

const loading = ref(false)
const loadingJobs = ref(false)
const timer = ref(refreshIntervalInSeconds)
const progress = computed(() => timer.value / refreshIntervalInSeconds * 100)
const jobs = ref<Job[]>([])
const importers: Map<number, ImporterDetail> = new Map<number, ImporterDetail>()
const apiLiveness = ref<boolean>()

const columns: DataTableColumn<Job>[] = [
  {
    type: 'selection'
  },
  {
    title: 'Id',
    key: 'id',
    render: (job) => h(NFlex, { align: 'center' }, () => [
      job.id,
      job.dependingOnId ? h(NTag, { size: 'small' }, { default: () => job.dependingOnId, icon: () => h(NIcon, { component: ArrowRouting20Regular }) }) : null
    ]),
    sorter: 'default'
  },
  {
    title: 'Type',
    key: 'type',
    render: (job) => h(NFlex, { align: 'center', justify: 'space-between' }, () => [
      toTitleCase(job.type),
      job.parameters ? h(NPopover, { trigger: 'click' }, {
        trigger: () => h(NButton, { size: 'tiny', renderIcon: () => h(Code20Regular) }),
        default: () => h(NCode, {
          code: JSON.stringify(JSON.parse(job.parameters ?? ''), null, 2),
          language: 'json',
          showLineNumbers: true
        })
      }) : null
    ]),
    sorter: 'default'
  },
  {
    title: 'Status',
    key: 'status',
    render: (job) => {
      switch (job.status) {
        case JobStatuses.InProgress:
          return h(NTag, { type: 'info', size: 'small' }, { default: () => 'In progress', avatar: () => h(NSpin, { size: 10 }) })

        case JobStatuses.Failed:
          return h(NTooltip, null, {
            trigger: () => h(NTag, {
              type: 'error',
              size: 'small'
            }, {
              default: () => `${job.status} (${job.failedCount} ${job.failedCount === 1 ? 'time' : 'times'})`
            }),
            default: () => `Last failed ${formatDateTime(job.lastFailedOn)}`
          })

        case JobStatuses.Completed:
          return h(NTag, { type: 'success', size: 'small' }, { default: () => job.status } )

        case JobStatuses.Pending:
          return h(NTooltip, { trigger: 'hover' }, {
            default: () => `Postponed until: ${job.postponeUntil ? formatDateTime(job.postponeUntil) : 'Unknown'}`,
            trigger: () => h(NTag, { size: 'small' }, { default: () => job.status } )
          })

        default:
          return h(NTag, { size: 'small' }, { default: () => toTitleCase(job.status) })
      }
    }
  },
  {
    title: 'Relation',
    key: 'relationId',
    render: (job) => {
      if (!job.relationId) return ''

      switch (job.relationKind) {
        case 'Importer': {
          const importer = importers.get(job.relationId)

          return `${importer?.groupName} (${importer?.typeName})`
        }

        default:
          return job.relationName
      }
    }
  },
  {
    title: 'Queued on',
    key: 'createdOn',
    align: 'right',
    width: 200,
    render: (job) => formatDateTime(job.createdOn),
    sorter: 'default',
    defaultSortOrder: 'descend'
  },
  {
    title: 'Started',
    key: 'startedOn',
    align: 'right',
    width: 150,
    render: (job) => formatTime(job.startedOn),
    sorter: 'default'
  },
  {
    title: 'Completed',
    key: 'completedOn',
    align: 'right',
    width: 200,
    render: (job) => formatTime(job.completedOn) + ` ${job.startedOn && job.completedOn ? '(' + formatDistanceStrict(new Date(job.startedOn), new Date(job.completedOn)) + ')' : ''}`,
    sorter: 'default'
  }
]

async function disableServiceMode() {
  loading.value = true

  try {
    await useCreate('service-mode/disable', null)
  } finally {
    loading.value = false
  }
}

async function refreshNow() {
  timer.value = refreshIntervalInSeconds
  defaultStore.checkIsServiceMode()
  apiLiveness.value = await checkHealth()

  if (isSuperAdmin.value && apiLiveness.value) {
    getJobs()
  }
}

async function getJobs() {
  loadingJobs.value = true

  try {
    const data = await useFetch('jobs', {
      statuses: [
        JobStatuses.Pending,
        JobStatuses.Failed,
        JobStatuses.Reserved,
        JobStatuses.InProgress
      ]
    })

    jobs.value = data
  } finally {
    loadingJobs.value = false
  }
}

async function getImporters() {
  const data = await useFetch('importers', {
    includeGroup: true
  })

  data.forEach((x: ImporterDetail) => {
    importers.set(x.id, x)
  })
}

watch(() => isSuperAdmin.value, async (value) => {
  if (value) {
    await getImporters()
    getJobs()
  }
})

onMounted(async () => {
  defaultStore.checkIsServiceMode()
  apiLiveness.value = await checkHealth()

  interval = setInterval(async () => {
    if (timer.value === 0) {
      timer.value = refreshIntervalInSeconds
      defaultStore.checkIsServiceMode()
      apiLiveness.value = await checkHealth()
    } else {
      timer.value--
    }
  }, 1000)
})

onBeforeUnmount(() => {
  clearInterval(interval)
})
</script>

<template>
  <n-layout>
    <n-flex vertical size="large" align="center" justify="center" class="w-screen h-screen p-6">
      <div class="fixed top-16 w-52">
        <img src="../assets/images/cdi_logo_white_on_blue.svg" alt="CDI">
      </div>
      <div class="text-4xl">
        Service mode
      </div>
      <div>
        The application is currently unavailable due to maintenance
      </div>
      <div class="mt-8 text-2xl">
        Next refresh in
      </div>
      <n-progress type="circle" :percentage="progress">
        <div class="text-center">
          {{ timer }} seconds
        </div>
      </n-progress>
      <n-flex class="mt-8">
        <n-button @click="refreshNow">
          Refresh now
        </n-button>
        <n-popconfirm v-if="apiLiveness && isSuperAdmin" :on-positive-click="disableServiceMode">
          <template #trigger>
            <n-button>
              Disable service mode
            </n-button>
          </template>
          Are you sure you want to disable service mode?
        </n-popconfirm>
      </n-flex>
      <div>
        If you have any questions please contact us at <a href="mailto:support@ipa.dk">support@ipa.dk</a>
      </div>
      <n-card v-if="apiLiveness && isSuperAdmin" title="Pending jobs" class="mt-4">
        <n-data-table
          :data="jobs"
          :columns="columns"
          :loading="loadingJobs"
          :row-key="(row: Job) => row.id"
          :pagination="{
            pageSize: 10
          }"
        />
      </n-card>
    </n-flex>
  </n-layout>
</template>